import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import { injectFollowUpWording } from '@modules/common/utils/follow-up-wording';
import { HttpErrorResponse } from '@angular/common/http';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { AlertService } from '@modules/alert/services/alert.service';
import { type RailMenuItem } from '@modules/common/components/rail-menu/rail-menu';
import { DeleteConfirmationModalService } from '@modules/common/services/delete-confirmation-modal.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { itemFollowStoreFor } from '@modules/common/services/item-follow/item-follow-store';
import { clearItemFollows, itemFollowErrorMessage } from '@modules/common/services/item-follow/item-follow-messages';
// Common
import { Component, inject, Injector, Input, type OnInit } from '@angular/core';
import { CurrentUserStore } from '@frontendlib/common/services/current-user.store';

// Utils
import { Like } from '@modules/common/types/like';
import { checkExhaustiveness } from '@modules/common/utils/switch';

// Types
import { Application } from '@modules/common/types/application';
import { Note } from '@modules/notes/types/note';
import { Notebook } from '@modules/notes/types/notebook';
import { NotebooksFilters } from '@modules/notes/types/notebooks-filters';
import { NotesFilters } from '@modules/notes/types/notes-filters';

// Services
import { NotebooksService } from '@modules/notes/services/notebooks.service';
import { NotesService } from '@modules/notes/services/notes.service';

// Components
import { BaseSidebarContextMenuComponent } from '@modules/common/components/base-sidebar-context-menu/base-sidebar-context-menu.component';
import { CanPurgePipe, PURGE_REFUSED_REASON } from '@modules/common/pipes/can-purge.pipe';
import { StitchType } from '@sharedlib/common/types/StitchType';

// RX
import { firstValueFrom, take } from 'rxjs';

import { type NotesRailKey, type NotesRailScope, notesRowMenu } from './notes-rail-menu';
import { collectRailIds } from './notes-rail-selection';
import { clearRailFollows } from './rail-clear-follows';

@Component({
  selector: 'app-sidebar-note-context-menu',
  templateUrl: './sidebar-context-menu.component.html',
  styleUrls: [
    '../../../common/components/base-sidebar-context-menu/base-sidebar-context-menu.component.less',
    './sidebar-context-menu.component.less',
  ],
  // Resolved here so the rail's purge refusal asks the very pipe the old "Empty trash" entry asks.
  providers: [CanPurgePipe],
  standalone: false,
})
export class SidebarContextMenuComponent
  extends BaseSidebarContextMenuComponent<StitchType.NOTE, StitchType.NOTEBOOK, NotesRailKey>
  implements OnInit
{
  protected readonly followUpWording = injectFollowUpWording();
  advancedSearchStateKey = Application.notes;

  /** A workspace rail row: the shared rail menu (`notes-rail-menu.ts`). Other menus keep the template below it. */
  @Input() public rail = false;
  /** Whether the row belongs to the member's My Workspace or to the Team Workspace. */
  @Input() public railScope: NotesRailScope = 'mine';

  /** "New note" / "New notebook" on a rail row: plain drafts in the row's tenant, never the row's marker or the selection. */
  protected railNewNote = new Note();
  protected railNewNotebook = new Notebook();

  private readonly injector = inject(Injector);
  private readonly canPurge = inject(CanPurgePipe);
  private railMenuCache: { key: string; menu: readonly RailMenuItem[] | undefined } | undefined;
  private readonly spaceContextStore = inject(SpaceContextStore);
  private readonly accountKind = inject(AccountKindService);
  protected get isTeamRail(): boolean {
    return this.tenantId != null && this.accountKind.isTeam() && this.spaceContextStore.insideTeam();
  }
  private readonly followToaster = inject(ToasterService);
  protected clearMySubscriptions(): void {
    clearItemFollows(itemFollowStoreFor(this.itemsService), this.tenantId, this.followToaster).subscribe();
  }

  /** The reason the disabled "Empty trash" entry carries (#1130 r2). */
  protected readonly PURGE_REFUSED_REASON = PURGE_REFUSED_REASON;

  private readonly currentUserStore = inject(CurrentUserStore);

  constructor(
    private readonly notesService: NotesService,
    private readonly notebooksService: NotebooksService,
  ) {
    super(notesService, notebooksService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    // Rail rows only: the old factories seed the draft from the search selection, which a row must never inherit.
    this.railNewNote = new Note({ accountId: this.tenantId });
    this.railNewNotebook = new Notebook({ accountId: this.tenantId });
  }

  /**
   * The tenant the purge gate asks CASL about — **never** the tenant an action is sent to.
   *
   * #1143 review F1. `tenantId` is bound only by the rail's in-a-team template
   * (`[appSidebarContextMenuTenantId]="team?.id"`); the flat private rail binds nothing at all, and
   * `team` is `undefined` on that rail in either shape, so the input arrives nullish in the private
   * profile. `CanPurgePipe` answers "no" for a tenant it cannot name — **before** it asks CASL — so the
   * gate disabled "Empty trash" in the one place the 2026-09-10 addendum says permanent delete ALWAYS
   * works, for every account including admins, whose unconditioned `*` rule was never consulted.
   *
   * The fallback belongs here and **not** in the pipe: "no tenant means no" is what stops the pipe from
   * asking the tenant-blind question this round exists to close. On the personal rail the caller's own
   * tenant is the signed-in account — the same tenant `getCommonParams` leaves for the server to resolve
   * (`accountId: undefined`, deliberately untouched below) and the tenant notes-app's personal rule
   * names verbatim, `conditions: { accountId: permissionsRequest.accountId }` carrying `purge`.
   *
   * A getter rather than a `computed()`: `tenantId` is a plain `@Input`, assigned imperatively by
   * `SidebarContextMenuDirective.registerInstance` and not a signal, so a memo would answer for whatever
   * tenant the menu happened to carry when it was first read. `appCanPurge` is `pure: false` beside it,
   * so the read costs the same either way.
   */
  protected get purgeTenantId(): string | null | undefined {
    return this.tenantId ?? this.currentUserStore.accountId();
  }

  protected getCommonParams(): Like<Note | Notebook> {
    // A rail row acts on THAT ROW's tenant with a plain draft, whatever the search has selected.
    if (this.rail) return { accountId: this.tenantId };

    let params: Like<Note | Notebook> = { accountId: this.tenantId };

    switch (this.sidebarFilterKey) {
      case 'following':
      case 'assigned_to_me':
      case 'shared':
      case 'search_results':
      case 'all_notes':
      case 'all_notebooks':
        break;
      case 'followed':
      case 'archived':
      case 'deleted':
      case 'snoozed':
      case 'flagged':
        params = super.getCommonParams();
        break;
      default:
        checkExhaustiveness(this.sidebarFilterKey);
    }

    return params;
  }

  /** Whether this person may destroy notes AND notebooks in the row's tenant: the dual gate of the old "Empty trash". */
  private get purgeAllowed(): boolean {
    const tenant = this.purgeTenantId;
    return this.canPurge.transform(tenant, 'Note') && this.canPurge.transform(tenant, 'Notebook');
  }

  /** The rail menu of this row, or `undefined` for a row that keeps the template's own menu. */
  protected get railMenu(): readonly RailMenuItem[] | undefined {
    if (!this.rail) return undefined;
    const purgeRefused = this.sidebarFilterKey === 'deleted' && !this.purgeAllowed ? PURGE_REFUSED_REASON : undefined;
    const key = `${this.sidebarFilterKey}|${this.railScope}|${purgeRefused ?? ''}`;
    if (this.railMenuCache?.key !== key) {
      this.railMenuCache = { key, menu: notesRowMenu(this.sidebarFilterKey, this.railScope, { purgeRefused }) };
    }
    return this.railMenuCache.menu;
  }

  /** "View settings…" on this row: the card for the list the row opens. */
  protected openRowViewSettings(): void {
    this.openViewSettings(this.sidebarFilterKey === 'all_notebooks' ? 'notebooks' : 'notes');
  }

  protected runRailEntry(id: string): void {
    const entry = this.railMenu?.find((candidate) => candidate?.id === id);
    // The shared renderer already answers Coming soon and refuses a disabled entry; a direct call must too.
    if (!entry || entry.comingSoon || entry.disabled) return;
    switch (id) {
      case 'settings':
        this.openRowViewSettings();
        return;
      case 'clear':
        if (this.sidebarFilterKey === 'following' && this.railScope === 'team') void this.clearRailSubscriptions();
        else this.clearRailRow();
        return;
      case 'clear-team':
        this.clearRailRow();
        return;
      case 'trash-archive':
      case 'delete-all':
        void this.confirmTrashArchive();
        return;
      case 'empty-trash':
        this.purgeRailTrash(false);
        return;
      case 'empty-trash-all':
        this.purgeRailTrash(true);
        return;
    }
  }

  /** Clears only the clicked row's visible notes and notebooks in the row's tenant, as of one fixed instant. */
  private clearRailRow(): void {
    const key = this.sidebarFilterKey;
    if (key !== 'flagged' && key !== 'followed' && key !== 'snoozed') return;
    const cutoff = new Date();
    const notes = new NotesFilters({ railPostponedTill: cutoff }).applyVirtualFolder(key);
    const books = new NotebooksFilters({ railPostponedTill: cutoff }).applyVirtualFolder(key);
    const noteConfig = { tenantItems: [this.railNewNote] };
    const bookConfig = { tenantItems: [this.railNewNotebook] };
    if (key === 'flagged') {
      this.itemsService.flag(notes, false, noteConfig);
      this.containerItemsService.flag(books, false, bookConfig);
    } else if (key === 'followed') {
      this.itemsService.followUp(notes, null, noteConfig);
      this.containerItemsService.followUp(books, null, bookConfig);
    } else {
      this.itemsService.snooze(notes, null, noteConfig);
      this.containerItemsService.snooze(books, null, bookConfig);
    }
  }

  private railWorkspaceName(): string {
    return this.railScope === 'team' ? 'Team Workspace' : 'My Workspace';
  }

  /**
   * Reads the archive of the clicked row completely, asks, and then moves exactly the ids it read: the trash
   * endpoints take ids only, so an archived-state filter is not something they can execute.
   */
  private async confirmTrashArchive(): Promise<void> {
    const tenant = this.tenantId;
    if (!tenant) return;
    // Captured now: the menu closes on this click and everything below outlives it.
    const toaster = this.followToaster;
    const alerts = this.injector.get(AlertService);
    const notes = this.notesService;
    const books = this.notebooksService;
    const noteDraft = this.railNewNote;
    const bookDraft = this.railNewNotebook;
    const team = this.railScope === 'team';
    const workspace = this.railWorkspaceName();
    const cutoff = new Date();

    let noteIds: string[];
    let bookIds: string[];
    try {
      noteIds = await collectRailIds(tenant, (offset, limit) =>
        notes.search(new NotesFilters({ railPostponedTill: cutoff, offset, limit }).applyVirtualFolder('archived'), {
          tenantItems: [noteDraft],
        }),
      );
      bookIds = await collectRailIds(tenant, (offset, limit) =>
        books.search(
          new NotebooksFilters({ railPostponedTill: cutoff, offset, limit }).applyVirtualFolder('archived'),
          { tenantItems: [bookDraft] },
        ),
      );
    } catch (error) {
      this.showRailError(toaster, error);
      return;
    }
    if (!noteIds.length && !bookIds.length) {
      toaster.show({ text: 'Archive is empty.' });
      return;
    }

    const counts = [
      noteIds.length ? `${noteIds.length} ${noteIds.length === 1 ? 'note' : 'notes'}` : undefined,
      bookIds.length ? `${bookIds.length} ${bookIds.length === 1 ? 'notebook' : 'notebooks'}` : undefined,
    ]
      .filter(Boolean)
      .join(' and ');
    const body =
      `Archived notes and notebooks in ${workspace} move to Trash. You can restore them from Trash. ` +
      `This moves ${counts}.` +
      (team ? ' This changes shared items for everyone in the team.' : '');

    alerts.show({
      title: 'Move to trash',
      body,
      rightButtons: [
        { title: 'CANCEL', close: true },
        {
          title: 'MOVE TO TRASH',
          close: true,
          click: () => {
            void this.moveToTrash(toaster, { notes, noteIds, noteDraft, books, bookIds, bookDraft });
          },
        },
      ],
    });
  }

  private async moveToTrash(
    toaster: ToasterService,
    {
      notes,
      noteIds,
      noteDraft,
      books,
      bookIds,
      bookDraft,
    }: {
      notes: NotesService;
      noteIds: readonly string[];
      noteDraft: Note;
      books: NotebooksService;
      bookIds: readonly string[];
      bookDraft: Notebook;
    },
  ): Promise<void> {
    try {
      for (let start = 0; start < noteIds.length; start += 100) {
        const ids = noteIds.slice(start, start + 100);
        await firstValueFrom(notes.delete(new NotesFilters({ ids }), true, undefined, { tenantItems: [noteDraft] }));
      }
      for (let start = 0; start < bookIds.length; start += 100) {
        const ids = bookIds.slice(start, start + 100);
        await firstValueFrom(
          books.delete(new NotebooksFilters({ ids }), true, undefined, { tenantItems: [bookDraft] }),
        );
      }
    } catch (error) {
      // The service has already told the person what the server said; a partial move is not reported as done.
      this.showRailError(toaster, error);
    }
  }

  /** A failed read or write: the services already toast an HTTP error, so only our own wording is added. */
  private showRailError(toaster: ToasterService, error: unknown): void {
    if (error instanceof HttpErrorResponse) return;
    toaster.show({ text: itemFollowErrorMessage(error) });
  }

  /**
   * "Review items to delete permanently…" (notes only) and the old "Empty trash" (notes and notebooks): a
   * team's purge first says it is for everyone, then the existing confirmation follows.
   */
  private purgeRailTrash(withNotebooks: boolean): void {
    if (!this.purgeAllowed) return;
    // Captured now: the menu closes on this click and the person answers afterwards.
    const confirmation = this.injector.get(DeleteConfirmationModalService);
    const alerts = this.injector.get(AlertService);
    const notes = this.notesService;
    const books = this.notebooksService;
    const noteDraft = this.railNewNote;
    const bookDraft = this.railNewNotebook;
    const cutoff = new Date();
    const noteFilter = new NotesFilters({ railPostponedTill: cutoff }).applyVirtualFolder('deleted');
    const bookFilter = new NotebooksFilters({ railPostponedTill: cutoff }).applyVirtualFolder('deleted');
    const allowed = (): boolean => this.purgeAllowed;

    const confirmAndPurge = (): void => {
      if (!allowed()) return;
      const confirmed$ = withNotebooks
        ? confirmation.confirm(StitchType.NOTEBOOK, { isEmptyTrash: true })
        : confirmation.confirm(StitchType.NOTE, { multiple: true });
      confirmed$.pipe(take(1)).subscribe(() => {
        if (!allowed()) return;
        notes.deletePermanently(noteFilter, { skipConfirmation: true, tenantItems: [noteDraft] });
        if (withNotebooks) {
          books.deletePermanently(bookFilter, { skipConfirmation: true, tenantItems: [bookDraft] });
        }
      });
    };

    if (this.railScope !== 'team') {
      confirmAndPurge();
      return;
    }
    alerts.show({
      title: 'Delete permanently',
      body: 'Items in Team Workspace will be permanently deleted for everyone in the team. This cannot be undone.',
      rightButtons: [
        { title: 'CANCEL', close: true },
        { title: 'CONTINUE', close: true, click: confirmAndPurge },
      ],
    });
  }

  /**
   * Team Following · Clear my subscriptions. The whole-list DELETE would also clear follows of archived and
   * trashed items the view never shows, so this reads what the view lists first and unfollows those, one by one.
   */
  private async clearRailSubscriptions(): Promise<void> {
    const tenant = this.tenantId;
    if (this.railScope !== 'team' || !tenant || tenant !== this.spaceContextStore.contextId()) return;
    const notes = this.notesService;
    const books = this.notebooksService;
    await clearRailFollows(
      tenant,
      this.spaceContextStore,
      [
        {
          list: (offset, limit) => notes.follow.api.list(tenant, offset, limit),
          unfollow: (id) => notes.follow.api.unfollow(id, tenant),
          record: (id) => notes.follow.record(id, tenant, false, false),
          refresh: () => notes.forceRefresh(),
        },
        {
          list: (offset, limit) => books.follow.api.list(tenant, offset, limit),
          unfollow: (id) => books.follow.api.unfollow(id, tenant),
          record: (id) => books.follow.record(id, tenant, false, false),
          refresh: () => books.forceRefresh(),
        },
      ],
      this.followToaster,
    );
  }

  protected stitchItemFactory(params: Like<Note>): Note {
    return new Note({ ...params, notebookId: this.selectedContainerStitchItemId });
  }

  protected containerStitchItemFactory(params: Like<Notebook>): Notebook {
    return new Notebook({ ...params, parentId: this.selectedContainerStitchItemId });
  }
}
